import { useState, useEffect, useCallback } from "react";
import { useAuth } from "../context/AuthContext";
import { Navbar } from "../components/Navbar";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";
import axios from "axios";
import { API_URL } from "../config/api";
import {
  Calendar,
  ChevronLeft,
  ChevronRight,
  Plus,
  X,
  Users,
  AlertTriangle,
  Loader2,
  Briefcase,
  Home,
  Plane,
  Clock,
  UserMinus,
  Coffee,
  CalendarDays,
  Send,
  Cake,
  ClipboardCheck,
  UserPlus,
  Trash2,
  Edit,
  MoreHorizontal,
} from "lucide-react";

// Leave type configurations
const LEAVE_TYPES = {
  holiday_leave: { label: "Holiday Leave", color: "#22c55e", icon: Plane },
  sick_leave: { label: "Sick Leave", color: "#ef4444", icon: UserMinus },
  maternity_leave: { label: "Maternity Leave", color: "#f472b6", icon: UserMinus },
  non_paid_leave: { label: "Non-Paid Leave", color: "#f59e0b", icon: Calendar },
  late_arrival: { label: "Late Arrival", color: "#3b82f6", icon: Clock },
  early_departure: { label: "Early Departure", color: "#8b5cf6", icon: Clock },
  work_from_home: { label: "Work From Home", color: "#06b6d4", icon: Home },
  business_trip: { label: "Business Trip", color: "#6366f1", icon: Briefcase },
  other_absence: { label: "Other Absence", color: "#64748b", icon: Coffee },
};

const MONTH_NAMES = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December"
];

// Calendar Day Cell Component
function CalendarDay({ date, events, isToday, isCurrentMonth, onClick, onEditEvent }) {
  const day = date.getDate();
  
  const handleEventClick = (e, event) => {
    e.stopPropagation(); // Prevent day click
    if (event.type === 'leave' && event.id) {
      onEditEvent(event);
    }
  };
  
  return (
    <div
      className={`min-h-28 p-1 border-b border-r cursor-pointer transition-colors hover:bg-slate-50
        ${!isCurrentMonth ? "bg-slate-100/50 text-slate-400" : ""}
        ${isToday ? "bg-blue-50 ring-2 ring-blue-400 ring-inset" : ""}`}
      onClick={() => onClick(date)}
    >
      <div className={`text-sm font-medium mb-1 ${isToday ? "text-blue-600" : ""}`}>
        {day}
      </div>
      <div className="space-y-0.5">
        {events.slice(0, 4).map((event, idx) => (
          <div
            key={idx}
            className={`text-xs px-1 py-0.5 rounded truncate text-white group relative ${event.type === 'leave' ? 'cursor-pointer hover:ring-2 hover:ring-white/50' : ''}`}
            style={{ backgroundColor: event.color || '#64748b' }}
            title={`${event.title}${event.type === 'leave' ? ' (click to edit)' : ''}`}
            onClick={(e) => handleEventClick(e, event)}
          >
            {event.title?.replace(/^(🎂|🏖️|🔍)\s*/, '').substring(0, 20)}
            {event.type === 'leave' && (
              <Edit className="h-3 w-3 absolute right-1 top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 transition-opacity" />
            )}
          </div>
        ))}
        {events.length > 4 && (
          <div className="text-xs text-slate-500 px-1">
            +{events.length - 4} more
          </div>
        )}
      </div>
    </div>
  );
}

// Leave Entry Modal
function LeaveEntryModal({ isOpen, onClose, onSave, staff, editingEntry }) {
  const [formData, setFormData] = useState({
    staff_id: "",
    leave_type: "holiday_leave",
    start_date: "",
    end_date: "",
    start_time: "",
    end_time: "",
    reason: "",
    backup_staff_id: "",
    notes: "",
    all_day: true,
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (editingEntry) {
      setFormData({
        staff_id: editingEntry.staff_id || "",
        leave_type: editingEntry.leave_type || "holiday_leave",
        start_date: editingEntry.start_date || "",
        end_date: editingEntry.end_date || "",
        start_time: editingEntry.start_time || "",
        end_time: editingEntry.end_time || "",
        reason: editingEntry.reason || "",
        backup_staff_id: editingEntry.backup_staff_id || "",
        notes: editingEntry.notes || "",
        all_day: editingEntry.all_day ?? true,
      });
    } else {
      setFormData({
        staff_id: "",
        leave_type: "holiday_leave",
        start_date: new Date().toISOString().split("T")[0],
        end_date: new Date().toISOString().split("T")[0],
        start_time: "",
        end_time: "",
        reason: "",
        backup_staff_id: "",
        notes: "",
        all_day: true,
      });
    }
  }, [editingEntry, isOpen]);

  const handleSave = async () => {
    if (!formData.staff_id) {
      toast.error("Please select a staff member");
      return;
    }
    if (!formData.start_date || !formData.end_date) {
      toast.error("Please select start and end dates");
      return;
    }

    setSaving(true);
    try {
      await onSave(formData, editingEntry?.id);
      onClose();
    } catch (error) {
      toast.error(error.message || "Failed to save leave entry");
    } finally {
      setSaving(false);
    }
  };

  const isPartialDay = ["late_arrival", "early_departure"].includes(formData.leave_type);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-4 border-b">
          <h2 className="text-lg font-semibold">
            {editingEntry ? "Edit Leave Entry" : "Add Leave Entry"}
          </h2>
          <Button variant="ghost" size="sm" onClick={onClose}>
            <X className="h-4 w-4" />
          </Button>
        </div>

        <div className="p-4 space-y-4">
          <div>
            <Label>Staff Member *</Label>
            <Select
              value={formData.staff_id}
              onValueChange={(v) => setFormData({ ...formData, staff_id: v })}
            >
              <SelectTrigger data-testid="leave-staff-select">
                <SelectValue placeholder="Select staff member" />
              </SelectTrigger>
              <SelectContent>
                {staff.map((s) => (
                  <SelectItem key={s.id} value={s.id}>
                    {s.first_name} {s.last_name} - {s.department}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label>Leave Type *</Label>
            <Select
              value={formData.leave_type}
              onValueChange={(v) => setFormData({ ...formData, leave_type: v, all_day: !["late_arrival", "early_departure"].includes(v) })}
            >
              <SelectTrigger data-testid="leave-type-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {Object.entries(LEAVE_TYPES).map(([key, config]) => (
                  <SelectItem key={key} value={key}>
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full" style={{ backgroundColor: config.color }} />
                      {config.label}
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Start Date *</Label>
              <Input
                type="date"
                value={formData.start_date}
                onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                data-testid="leave-start-date"
              />
            </div>
            <div>
              <Label>End Date *</Label>
              <Input
                type="date"
                value={formData.end_date}
                onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                data-testid="leave-end-date"
              />
            </div>
          </div>

          {isPartialDay && (
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>{formData.leave_type === "late_arrival" ? "Arrival Time" : "Departure Time"}</Label>
                <Input
                  type="time"
                  value={formData.leave_type === "late_arrival" ? formData.start_time : formData.end_time}
                  onChange={(e) => setFormData({
                    ...formData,
                    [formData.leave_type === "late_arrival" ? "start_time" : "end_time"]: e.target.value
                  })}
                />
              </div>
            </div>
          )}

          <div>
            <Label>Backup/Cover (Optional)</Label>
            <Select
              value={formData.backup_staff_id || "none"}
              onValueChange={(v) => setFormData({ ...formData, backup_staff_id: v === "none" ? "" : v })}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select backup person" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">None</SelectItem>
                {staff.filter(s => s.id !== formData.staff_id).map((s) => (
                  <SelectItem key={s.id} value={s.id}>
                    {s.first_name} {s.last_name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label>Reason (Optional)</Label>
            <Input
              value={formData.reason}
              onChange={(e) => setFormData({ ...formData, reason: e.target.value })}
              placeholder="e.g., Family vacation, Doctor appointment"
            />
          </div>

          <div>
            <Label>Notes (Optional)</Label>
            <Textarea
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              placeholder="Additional notes..."
              rows={2}
            />
          </div>
        </div>

        <div className="flex justify-end gap-2 p-4 border-t">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={handleSave} disabled={saving} data-testid="save-leave-btn">
            {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            {editingEntry ? "Update" : "Add"} Leave Entry
          </Button>
        </div>
      </div>
    </div>
  );
}

// Add/Edit Staff Modal (Onboarding)
function StaffModal({ isOpen, onClose, onSave, editingStaff }) {
  const [formData, setFormData] = useState({
    firstname: "",
    surname: "",
    email: "",
    mobile: "",
    phone: "",
    job_title: "",
    birthday: "",
    start_date: "",
    company: "Europacific d.o.o.",
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (editingStaff) {
      setFormData({
        firstname: editingStaff.firstname || "",
        surname: editingStaff.surname || "",
        email: editingStaff.email || "",
        mobile: editingStaff.mobile || "",
        phone: editingStaff.phone || "",
        job_title: editingStaff.job_title || "",
        birthday: editingStaff.birthday || "",
        start_date: editingStaff.start_date || "",
        company: editingStaff.company || "Europacific d.o.o.",
      });
    } else {
      setFormData({
        firstname: "",
        surname: "",
        email: "",
        mobile: "",
        phone: "",
        job_title: "",
        birthday: "",
        start_date: new Date().toISOString().split("T")[0],
        company: "Europacific d.o.o.",
      });
    }
  }, [editingStaff, isOpen]);

  const handleSave = async () => {
    if (!formData.firstname || !formData.surname) {
      toast.error("First name and surname are required");
      return;
    }

    setSaving(true);
    try {
      await onSave(formData, editingStaff?.id);
      onClose();
    } catch (error) {
      toast.error(error.message || "Failed to save staff");
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-4 border-b">
          <h2 className="text-lg font-semibold flex items-center gap-2">
            <UserPlus className="h-5 w-5 text-green-600" />
            {editingStaff ? "Edit Staff Member" : "Add New Staff (Onboarding)"}
          </h2>
          <Button variant="ghost" size="sm" onClick={onClose}>
            <X className="h-4 w-4" />
          </Button>
        </div>

        <div className="p-4 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>First Name *</Label>
              <Input
                value={formData.firstname}
                onChange={(e) => setFormData({ ...formData, firstname: e.target.value })}
                placeholder="John"
                data-testid="staff-firstname"
              />
            </div>
            <div>
              <Label>Surname *</Label>
              <Input
                value={formData.surname}
                onChange={(e) => setFormData({ ...formData, surname: e.target.value })}
                placeholder="Doe"
                data-testid="staff-surname"
              />
            </div>
          </div>

          <div>
            <Label>Email</Label>
            <Input
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              placeholder="john.doe@europacific.com"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Mobile</Label>
              <Input
                value={formData.mobile}
                onChange={(e) => setFormData({ ...formData, mobile: e.target.value })}
                placeholder="+386 40 123 456"
              />
            </div>
            <div>
              <Label>Phone</Label>
              <Input
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                placeholder="+386 5 123 4567"
              />
            </div>
          </div>

          <div>
            <Label>Job Title</Label>
            <Input
              value={formData.job_title}
              onChange={(e) => setFormData({ ...formData, job_title: e.target.value })}
              placeholder="e.g., Operations Manager"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Birthday</Label>
              <Input
                type="date"
                value={formData.birthday}
                onChange={(e) => setFormData({ ...formData, birthday: e.target.value })}
              />
            </div>
            <div>
              <Label>Start Date</Label>
              <Input
                type="date"
                value={formData.start_date}
                onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
              />
            </div>
          </div>

          <div>
            <Label>Vertical</Label>
            <Select
              value={formData.company}
              onValueChange={(v) => setFormData({ ...formData, company: v })}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Europacific Logistika d.o.o.">Europacific Logistika</SelectItem>
                <SelectItem value="EP Holding d.o.o.">EP Holding</SelectItem>
                <SelectItem value="Cargo CFS d.o.o.">Cargo CFS</SelectItem>
                <SelectItem value="Artcom EPT d.o.o.">Artcom EPT</SelectItem>
                <SelectItem value="AM-Tech 2.0 d.o.o.">AM-Tech 2.0</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="flex justify-end gap-2 p-4 border-t">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={handleSave} disabled={saving} data-testid="save-staff-btn">
            {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            {editingStaff ? "Update" : "Add"} Staff
          </Button>
        </div>
      </div>
    </div>
  );
}

// Staff Directory Panel with Offboarding
function StaffDirectoryPanel({ staffDirectory, onEdit, onOffboard, onRefresh }) {
  const [search, setSearch] = useState("");
  const [showAll, setShowAll] = useState(false);
  
  const filtered = staffDirectory.filter(s => 
    s.full_name?.toLowerCase().includes(search.toLowerCase()) ||
    s.job_title?.toLowerCase().includes(search.toLowerCase()) ||
    s.company?.toLowerCase().includes(search.toLowerCase())
  );
  
  const displayed = showAll ? filtered : filtered.slice(0, 5);
  
  // Get upcoming birthdays (next 30 days)
  const today = new Date();
  const upcomingBirthdays = staffDirectory.filter(s => {
    if (!s.birthday) return false;
    const bd = new Date(s.birthday);
    const thisYearBd = new Date(today.getFullYear(), bd.getMonth(), bd.getDate());
    if (thisYearBd < today) {
      thisYearBd.setFullYear(today.getFullYear() + 1);
    }
    const daysUntil = Math.ceil((thisYearBd - today) / (1000 * 60 * 60 * 24));
    return daysUntil <= 30 && daysUntil >= 0;
  }).map(s => {
    const bd = new Date(s.birthday);
    const thisYearBd = new Date(today.getFullYear(), bd.getMonth(), bd.getDate());
    if (thisYearBd < today) {
      thisYearBd.setFullYear(today.getFullYear() + 1);
    }
    const daysUntil = Math.ceil((thisYearBd - today) / (1000 * 60 * 60 * 24));
    return { ...s, daysUntil };
  }).sort((a, b) => a.daysUntil - b.daysUntil);

  return (
    <Card>
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2 text-lg">
            <Users className="h-5 w-5 text-blue-600" />
            Staff Directory ({staffDirectory.length})
          </CardTitle>
        </div>
      </CardHeader>
      <CardContent>
        {/* Upcoming birthdays */}
        {upcomingBirthdays.length > 0 && (
          <div className="mb-4 p-3 bg-pink-50 rounded-lg border border-pink-200">
            <div className="flex items-center gap-2 text-pink-700 font-medium text-sm mb-2">
              <Cake className="h-4 w-4" />
              Upcoming Birthdays
            </div>
            <div className="space-y-1">
              {upcomingBirthdays.slice(0, 3).map((s, idx) => (
                <div key={idx} className="text-sm flex items-center justify-between">
                  <span>{s.full_name}</span>
                  <Badge variant="outline" className="text-pink-600 border-pink-300">
                    {s.daysUntil === 0 ? "Today! 🎉" : `in ${s.daysUntil} days`}
                  </Badge>
                </div>
              ))}
            </div>
          </div>
        )}
        
        {/* Search */}
        <Input
          placeholder="Search staff..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="mb-3"
        />
        
        {/* Staff list */}
        <div className="space-y-2 max-h-64 overflow-y-auto">
          {displayed.map((s) => {
          // Shorten company name to show as vertical
          const verticalName = s.company?.replace(/\s*d\.o\.o\.?\s*/gi, '').trim() || '';
          return (
            <div key={s.id} className="flex items-center justify-between p-2 bg-slate-50 rounded-lg hover:bg-slate-100">
              <div className="flex-1 min-w-0">
                <div className="font-medium text-sm truncate">{s.full_name}</div>
                <div className="text-xs text-slate-500 truncate">
                  {s.job_title} {verticalName ? `• ${verticalName}` : ""}
                </div>
              </div>
              <div className="flex items-center gap-1">
                <Button variant="ghost" size="sm" onClick={() => onEdit(s)} title="Edit">
                  <Edit className="h-3 w-3" />
                </Button>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={() => onOffboard(s)} 
                  title="Offboard"
                  className="text-red-500 hover:text-red-700 hover:bg-red-50"
                >
                  <Trash2 className="h-3 w-3" />
                </Button>
              </div>
            </div>
          );
        })}
        </div>
        
        {filtered.length > 5 && (
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={() => setShowAll(!showAll)}
            className="w-full mt-2"
          >
            {showAll ? "Show less" : `Show all ${filtered.length} staff`}
          </Button>
        )}
      </CardContent>
    </Card>
  );
}

// Today's Events Panel
function TodayEventsPanel({ events, onEditEvent }) {
  if (!events || events.length === 0) return null;

  const birthdays = events.filter(e => e.type === 'birthday');
  const inspections = events.filter(e => e.type === 'inspection');
  const leaves = events.filter(e => e.type === 'leave');

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center gap-2 text-lg">
          <CalendarDays className="h-5 w-5 text-blue-600" />
          Today's Events ({events.length})
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {birthdays.length > 0 && (
            <div className="space-y-2">
              <div className="flex items-center gap-2 text-pink-600 font-medium text-sm">
                <Cake className="h-4 w-4" />
                Birthdays ({birthdays.length})
              </div>
              {birthdays.map((event, idx) => (
                <div key={idx} className="bg-pink-50 rounded-lg p-2 text-sm">
                  <div className="font-medium">{event.staff_name}</div>
                </div>
              ))}
            </div>
          )}
          
          {inspections.length > 0 && (
            <div className="space-y-2">
              <div className="flex items-center gap-2 text-red-600 font-medium text-sm">
                <ClipboardCheck className="h-4 w-4" />
                Inspections ({inspections.length})
              </div>
              {inspections.map((event, idx) => (
                <div key={idx} className="bg-red-50 rounded-lg p-2 text-sm">
                  <div className="font-medium">{event.pf_number}</div>
                  <div className="text-xs text-slate-500">{event.description}</div>
                </div>
              ))}
            </div>
          )}
          
          {leaves.length > 0 && (
            <div className="space-y-2">
              <div className="flex items-center gap-2 text-green-600 font-medium text-sm">
                <Plane className="h-4 w-4" />
                Staff Out ({leaves.length})
              </div>
              {leaves.map((event, idx) => (
                <div 
                  key={idx} 
                  className="rounded-lg p-2 text-sm cursor-pointer hover:ring-2 hover:ring-slate-300 transition-all group relative" 
                  style={{ backgroundColor: `${event.color}20` }}
                  onClick={() => onEditEvent && event.id && onEditEvent(event)}
                >
                  <div className="font-medium flex items-center justify-between">
                    {event.staff_name}
                    {event.id && (
                      <Edit className="h-3 w-3 text-slate-400 opacity-0 group-hover:opacity-100 transition-opacity" />
                    )}
                  </div>
                  <div className="text-xs text-slate-500">{LEAVE_TYPES[event.leave_type]?.label || event.leave_type}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

// Unhandled Tasks Alert Panel
function UnhandledTasksPanel({ tasks, onSendAlert }) {
  if (!tasks || tasks.length === 0) return null;

  return (
    <Card className="border-amber-200 bg-amber-50">
      <CardHeader className="pb-2">
        <CardTitle className="text-amber-800 flex items-center gap-2 text-lg">
          <AlertTriangle className="h-5 w-5" />
          Staff on Leave with Unhandled Tasks
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {tasks.map((task, idx) => (
          <div key={idx} className="bg-white rounded-lg p-3 border border-amber-200">
            <div className="flex items-center justify-between">
              <div>
                <div className="font-medium">{task.staff_name}</div>
                <div className="text-sm text-slate-600">
                  {task.department} • {task.leave_type_label}
                  {task.is_on_leave_now ? (
                    <Badge variant="destructive" className="ml-2">On Leave Now</Badge>
                  ) : (
                    <Badge variant="outline" className="ml-2">
                      Starts in {task.days_until_leave} day{task.days_until_leave !== 1 ? "s" : ""}
                    </Badge>
                  )}
                </div>
                <div className="text-sm text-amber-700 mt-1">
                  {task.pf_count} Position Folder{task.pf_count !== 1 ? "s" : ""} need attention
                </div>
              </div>
              <Button
                size="sm"
                variant="outline"
                className="border-amber-400 text-amber-700 hover:bg-amber-100"
                onClick={() => onSendAlert(task.staff_id)}
              >
                <Send className="h-4 w-4 mr-1" />
                Alert Team
              </Button>
            </div>
          </div>
        ))}
      </CardContent>
    </Card>
  );
}

export default function LeaveCalendarPage() {
  const { token, user, isAdmin } = useAuth();
  const [loading, setLoading] = useState(true);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [calendarData, setCalendarData] = useState({});
  const [staff, setStaff] = useState([]);
  const [staffDirectory, setStaffDirectory] = useState([]);
  const [showLeaveModal, setShowLeaveModal] = useState(false);
  const [showStaffModal, setShowStaffModal] = useState(false);
  const [editingEntry, setEditingEntry] = useState(null);
  const [editingStaff, setEditingStaff] = useState(null);
  const [unhandledTasks, setUnhandledTasks] = useState([]);
  const [todayEvents, setTodayEvents] = useState([]);
  
  // Filters
  const [showBirthdays, setShowBirthdays] = useState(true);
  const [showInspections, setShowInspections] = useState(true);
  const [showLeave, setShowLeave] = useState(true);

  const currentMonth = currentDate.getMonth();
  const currentYear = currentDate.getFullYear();

  const fetchCalendarData = useCallback(async () => {
    try {
      const startDate = new Date(currentYear, currentMonth, 1);
      const endDate = new Date(currentYear, currentMonth + 1, 0);
      
      // Use local date format to avoid timezone issues
      const formatDate = (d) => {
        const year = d.getFullYear();
        const month = String(d.getMonth() + 1).padStart(2, '0');
        const day = String(d.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
      };
      
      const params = new URLSearchParams({
        start_date: formatDate(startDate),
        end_date: formatDate(endDate),
        include_birthdays: showBirthdays,
        include_inspections: showInspections,
        include_leave: showLeave,
      });

      const response = await axios.get(`${API_URL}/staff-calendar?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setCalendarData(response.data.by_date || {});
      
      const today = formatDate(new Date());
      setTodayEvents(response.data.by_date?.[today] || []);
    } catch (error) {
      console.error("Error fetching calendar:", error);
      toast.error("Failed to load calendar data");
    }
  }, [token, currentMonth, currentYear, showBirthdays, showInspections, showLeave]);

  const fetchStaff = async () => {
    try {
      const response = await axios.get(`${API_URL}/staff`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setStaff(response.data.staff || []);
    } catch (error) {
      console.error("Error fetching staff:", error);
    }
  };

  const fetchStaffDirectory = async () => {
    try {
      const response = await axios.get(`${API_URL}/staff-directory`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setStaffDirectory(response.data.staff || []);
    } catch (error) {
      console.error("Error fetching staff directory:", error);
    }
  };

  const fetchUnhandledTasks = async () => {
    try {
      const response = await axios.get(`${API_URL}/leave/unhandled-tasks`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setUnhandledTasks(response.data.unhandled_tasks || []);
    } catch (error) {
      console.error("Error fetching unhandled tasks:", error);
    }
  };

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([
        fetchStaff(),
        fetchStaffDirectory(),
        fetchCalendarData(),
        fetchUnhandledTasks(),
      ]);
      setLoading(false);
    };
    loadData();
  }, [token]);

  useEffect(() => {
    fetchCalendarData();
  }, [fetchCalendarData]);

  const handleSaveLeaveEntry = async (formData, entryId) => {
    if (entryId) {
      await axios.put(`${API_URL}/leave/entries/${entryId}`, formData, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Leave entry updated");
    } else {
      await axios.post(`${API_URL}/leave/entries`, formData, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Leave entry added");
    }
    fetchCalendarData();
    fetchUnhandledTasks();
  };

  const handleEditEvent = (event) => {
    if (event.type === 'leave' && event.id) {
      // Convert event data to editing format
      setEditingEntry({
        id: event.id,
        staff_id: event.staff_id,
        leave_type: event.leave_type,
        start_date: event.start_date,
        end_date: event.end_date,
        start_time: event.start_time || "",
        end_time: event.end_time || "",
        reason: event.reason || "",
        backup_staff_id: event.backup_staff_id || "",
        notes: event.notes || "",
        all_day: event.all_day ?? true,
      });
      setShowLeaveModal(true);
    }
  };

  const handleSaveStaff = async (formData, staffId) => {
    if (staffId) {
      await axios.put(`${API_URL}/staff-directory/${staffId}`, formData, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Staff member updated");
    } else {
      await axios.post(`${API_URL}/staff-directory`, formData, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Staff member added (onboarded)");
    }
    fetchStaffDirectory();
    fetchCalendarData();
  };

  const handleOffboardStaff = async (staffMember) => {
    if (!window.confirm(`Are you sure you want to offboard ${staffMember.full_name}? This will remove them from the staff directory.`)) {
      return;
    }
    
    try {
      await axios.delete(`${API_URL}/staff-directory/${staffMember.id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success(`${staffMember.full_name} has been offboarded`);
      fetchStaffDirectory();
      fetchCalendarData();
    } catch (error) {
      toast.error("Failed to offboard staff member");
    }
  };

  const handleSendAlert = async (staffId) => {
    try {
      const response = await axios.post(
        `${API_URL}/leave/send-handover-alert/${staffId}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(response.data.message);
      fetchUnhandledTasks();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send alert");
    }
  };

  const navigateMonth = (direction) => {
    setCurrentDate(new Date(currentYear, currentMonth + direction, 1));
  };

  // Generate calendar grid
  // Helper to format date as YYYY-MM-DD without timezone shift
  const formatDateLocal = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const generateCalendarDays = () => {
    const firstDay = new Date(currentYear, currentMonth, 1);
    const lastDay = new Date(currentYear, currentMonth + 1, 0);
    const startDay = firstDay.getDay();
    const daysInMonth = lastDay.getDate();

    const days = [];
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    // Add previous month days to fill the first week
    const prevMonthLastDay = new Date(currentYear, currentMonth, 0).getDate();
    for (let i = startDay - 1; i >= 0; i--) {
      const date = new Date(currentYear, currentMonth - 1, prevMonthLastDay - i);
      const dateStr = formatDateLocal(date);
      days.push({
        date,
        events: calendarData[dateStr] || [],
        isToday: false,
        isCurrentMonth: false,
      });
    }

    // Add current month days
    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(currentYear, currentMonth, day);
      const dateStr = formatDateLocal(date);
      const isToday = date.getTime() === today.getTime();
      days.push({
        date,
        events: calendarData[dateStr] || [],
        isToday,
        isCurrentMonth: true,
      });
    }

    // Calculate how many rows we need (minimum 5, maximum 6)
    // Only add enough next month days to complete the final week
    const totalDaysSoFar = days.length;
    const remainingInLastWeek = totalDaysSoFar % 7 === 0 ? 0 : 7 - (totalDaysSoFar % 7);
    
    for (let i = 1; i <= remainingInLastWeek; i++) {
      const date = new Date(currentYear, currentMonth + 1, i);
      const dateStr = formatDateLocal(date);
      days.push({
        date,
        events: calendarData[dateStr] || [],
        isToday: false,
        isCurrentMonth: false,
      });
    }

    return days;
  };

  const calendarDays = generateCalendarDays();

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Navbar />
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar />
      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* Header */}
        <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-3">
              <Calendar className="h-7 w-7 text-blue-600" />
              Europacific Group Calendar
            </h1>
            <p className="text-slate-600 mt-1">
              All verticals: Europacific Logistika, EP Holding, Cargo CFS, Artcom EPT, AM-Tech 2.0
            </p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => { setEditingStaff(null); setShowStaffModal(true); }} data-testid="add-staff-btn">
              <UserPlus className="h-4 w-4 mr-2" />
              Add Staff
            </Button>
            <Button onClick={() => { setEditingEntry(null); setShowLeaveModal(true); }} data-testid="add-leave-btn">
              <Plus className="h-4 w-4 mr-2" />
              Add Leave
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Left sidebar - Staff Directory */}
          <div className="lg:col-span-1 space-y-4">
            <StaffDirectoryPanel
              staffDirectory={staffDirectory}
              onEdit={(s) => { setEditingStaff(s); setShowStaffModal(true); }}
              onOffboard={handleOffboardStaff}
              onRefresh={fetchStaffDirectory}
            />
          </div>

          {/* Main content - Calendar */}
          <div className="lg:col-span-3 space-y-4">
            {/* Alerts & Today's Events */}
            <UnhandledTasksPanel tasks={unhandledTasks} onSendAlert={handleSendAlert} />
            <TodayEventsPanel events={todayEvents} onEditEvent={handleEditEvent} />

            {/* Calendar */}
            <Card>
              <CardHeader className="pb-2">
                <div className="flex flex-wrap items-center justify-between gap-4">
                  <div className="flex items-center gap-4">
                    <Button variant="ghost" size="sm" onClick={() => navigateMonth(-1)}>
                      <ChevronLeft className="h-4 w-4" />
                    </Button>
                    <h2 className="text-xl font-semibold min-w-[180px] text-center">
                      {MONTH_NAMES[currentMonth]} {currentYear}
                    </h2>
                    <Button variant="ghost" size="sm" onClick={() => navigateMonth(1)}>
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                  
                  {/* Filters */}
                  <div className="flex items-center gap-3 flex-wrap">
                    <span className="text-sm text-slate-500">Show:</span>
                    <button
                      onClick={() => setShowBirthdays(!showBirthdays)}
                      className={`flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium transition-colors
                        ${showBirthdays ? 'bg-pink-100 text-pink-700' : 'bg-slate-100 text-slate-400'}`}
                    >
                      <Cake className="h-3 w-3" />
                      Birthdays
                    </button>
                    <button
                      onClick={() => setShowInspections(!showInspections)}
                      className={`flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium transition-colors
                        ${showInspections ? 'bg-red-100 text-red-700' : 'bg-slate-100 text-slate-400'}`}
                    >
                      <ClipboardCheck className="h-3 w-3" />
                      Inspections
                    </button>
                    <button
                      onClick={() => setShowLeave(!showLeave)}
                      className={`flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium transition-colors
                        ${showLeave ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-400'}`}
                    >
                      <Plane className="h-3 w-3" />
                      Leave
                    </button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-7 border-t border-l">
                  {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((day) => (
                    <div key={day} className="p-2 text-center text-sm font-medium text-slate-600 bg-slate-100 border-r border-b">
                      {day}
                    </div>
                  ))}
                </div>
                <div className="grid grid-cols-7 border-l">
                  {calendarDays.map((day, idx) => (
                    <CalendarDay
                      key={idx}
                      date={day.date}
                      events={day.events}
                      isToday={day.isToday}
                      isCurrentMonth={day.isCurrentMonth}
                      onClick={() => {
                        setEditingEntry(null);
                        setShowLeaveModal(true);
                      }}
                      onEditEvent={handleEditEvent}
                    />
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Legend */}
            <div className="flex flex-wrap gap-4">
              <div className="flex items-center gap-2 text-sm">
                <div className="w-3 h-3 rounded-full bg-pink-500" />
                <span className="text-slate-600">Birthday</span>
              </div>
              <div className="flex items-center gap-2 text-sm">
                <div className="w-3 h-3 rounded-full bg-red-500" />
                <span className="text-slate-600">Inspection</span>
              </div>
              {Object.entries(LEAVE_TYPES).map(([key, config]) => (
                <div key={key} className="flex items-center gap-2 text-sm">
                  <div className="w-3 h-3 rounded-full" style={{ backgroundColor: config.color }} />
                  <span className="text-slate-600">{config.label}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Leave Entry Modal */}
      <LeaveEntryModal
        isOpen={showLeaveModal}
        onClose={() => { setShowLeaveModal(false); setEditingEntry(null); }}
        onSave={handleSaveLeaveEntry}
        staff={staff}
        editingEntry={editingEntry}
      />
      
      {/* Staff Modal (Onboarding/Edit) */}
      <StaffModal
        isOpen={showStaffModal}
        onClose={() => { setShowStaffModal(false); setEditingStaff(null); }}
        onSave={handleSaveStaff}
        editingStaff={editingStaff}
      />
    </div>
  );
}
